/**
 * Project Service for PBS
 * Handles project-related API calls
 */

import apiClient from './apiClient';
import { Project } from '../types/api';

// Re-export Project type for convenience
export type { Project };

export interface ProjectsResponse {
  success: boolean;
  data: {
    projects: Project[];
  };
}

class ProjectService {
  /**
   * Get projects where user is Project Owner.
   * Redmine projects only - opportunities come from opportunityService.
   */
  async getMyPoProjects(): Promise<Project[]> {
    try {
      const response = await apiClient.get<ProjectsResponse>('/projects/my-po-projects');
      
      if (response.data.success) {
        return response.data.data.projects;
      }
      
      return [];
    } catch (error) {
      console.error('Failed to fetch PO projects:', error);
      throw error;
    }
  }

  /**
   * Get all projects (for admins)
   */
  async getAllProjects(): Promise<Project[]> {
    try {
      const response = await apiClient.get<{ data: Project[] }>('/projects/');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch all projects:', error);
      throw error;
    }
  }

  /**
   * Search projects
   */
  async searchProjects(query: string): Promise<Project[]> {
    try {
      const response = await apiClient.get<{ data: Project[] }>(`/projects/search?q=${encodeURIComponent(query)}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to search projects:', error);
      throw error;
    }
  }

  /**
   * Get project by ID
   */
  async getProjectById(projectId: number): Promise<Project | null> {
    try {
      const response = await apiClient.get<Project>(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch project ${projectId}:`, error);
      return null;
    }
  }

  /**
   * Get status label from status number
   */
  getStatusLabel(status: number): string {
    const statusMap: Record<number, string> = {
      1: 'Active',
      2: 'On Hold',
      3: 'Completed',
      5: 'Archived',
      9: 'Closed',
    };
    return statusMap[status] || 'Unknown';
  }
}

// Export singleton instance
const projectService = new ProjectService();
export default projectService;
